"use client"

import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { useEditorStore } from "@/lib/editor-store"
import { saveSessionIdCookieAction, clearSessionAction } from "@/app/actions/session"

export function CookieConsentDialog() {
  const [open, setOpen] = useState(false)
  const { state } = useEditorStore()

  useEffect(() => {
    // Check if consent cookie is already set
    if (typeof window !== "undefined") {
      const cookies = document.cookie.split(";").map((c) => c.trim())
      const hasConsentCookie = cookies.some((c) => c.startsWith("cookie_consent="))
      if (!hasConsentCookie) {
        setOpen(true)
      }
    }
  }, [])

  const handleAccept = async () => {
    // Set cookie_consent to true, 1 year expiration
    const expiry = new Date()
    expiry.setFullYear(expiry.getFullYear() + 1)
    document.cookie = `cookie_consent=true; expires=${expiry.toUTCString()}; path=/; sameSite=lax`
    setOpen(false)

    // If there is already an active session ID in the editor store state,
    // save it in the secure httpOnly cookie via Server Action
    if (state.sessionId) {
      try {
        await saveSessionIdCookieAction(state.sessionId, true)
        console.log("[Cookie Consent] Active session saved to cookies.")
      } catch (err) {
        console.error("[Cookie Consent] Failed to save active session to cookies:", err)
      }
    }
  }

  const handleDecline = async () => {
    // Set cookie_consent to false, 1 year expiration (so we don't show the dialog again)
    const expiry = new Date()
    expiry.setFullYear(expiry.getFullYear() + 1)
    document.cookie = `cookie_consent=false; expires=${expiry.toUTCString()}; path=/; sameSite=lax`
    setOpen(false)

    // Clear any existing session cookie via Server Action
    try {
      await clearSessionAction()
      console.log("[Cookie Consent] Cookies declined, cleared session cookie.")
    } catch (err) {
      console.error("[Cookie Consent] Failed to clear session cookie:", err)
    }
  }

  if (!open) return null

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-3 max-w-sm p-4 bg-[var(--ed-cookie-bg)] border border-[var(--ed-cookie-border)] rounded-lg shadow-2xl animate-in fade-in slide-in-from-bottom-5 duration-300">
      <div className="flex flex-col gap-1.5">
        <h3 className="text-sm font-semibold text-[var(--ed-cookie-title-text)] flex items-center gap-2 select-none">
          🍪 Cookie Consent
        </h3>
        <p className="text-[var(--ed-cookie-body-text)] text-xs leading-relaxed select-none">
          We use cookies to save and restore your active code editor sessions and auto-compile preferences. This ensures your progress is saved across page reloads.
        </p>
      </div>
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={handleDecline}
          className="text-xs h-7 px-3 border-[var(--ed-cookie-btn-border)] hover:bg-[var(--ed-cookie-btn-hover)]"
        >
          Decline
        </Button>
        <Button
          onClick={handleAccept}
          className="text-xs h-7 px-3 bg-[#5B8CFF] hover:bg-[#4676E5] text-white font-medium rounded transition-colors"
        >
          Accept Cookies
        </Button>
      </div>
    </div>
  )
}
